'use client';

// Shared chat surface (web): message list + composer. Used by the global Live
// room (roomId=null, ephemeral 24h) and by DM/group rooms (persistent,
// member-only). Realtime-first with a 5s poll fallback for private rooms —
// if Realtime can't validate the user's ES256 token it downgrades to anon and
// member-only RLS silently drops room events.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ImagePlus, Send, Flag, Ban, X, CornerUpLeft, Users, AtSign, ChevronDown, ChevronUp, Pin, PinOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/lib/i18n/LanguageContext';
import MentionTextarea from '@/app/forum/MentionTextarea';
import {
  blockUser,
  canPinLiveMessages,
  fetchBlockedIds,
  fetchLiveChatReactions,
  fetchPinnedMessages,
  fetchRecentMessages,
  MAX_MESSAGE_LEN,
  pinLiveMessage,
  reportMessage,
  sendMessage,
  subscribeToChat,
  toggleLiveChatReaction,
  updateChatPresence,
  uploadChatImage,
  type LiveChatMessage,
  type PresenceUser,
  type ReactionSummary,
} from '@/lib/liveChat';

const ROOM_POLL_MS = 5000;
const SEND_COOLDOWN_MS = 2000;

// Reaction set shown under every message — like/dislike are mutually exclusive server-side,
// the rest (fire/hundred/laugh) are independent toggles. Order = display order.
const REACTIONS: { key: ReactionSummary['reaction']; emoji: string; label: string }[] = [
  { key: 'like', emoji: '👍', label: 'Like' },
  { key: 'dislike', emoji: '👎', label: 'Dislike' },
  { key: 'fire', emoji: '🔥', label: 'Fire' },
  { key: 'hundred', emoji: '💯', label: 'Spot on' },
  { key: 'laugh', emoji: '😂', label: 'Haha' },
];

// @username → clickable profile link, inside a message bubble.
const MENTION_RE = /(^|[\s(])@([A-Za-z0-9_.]{2,30})/g;
function Linkified({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(MENTION_RE)) {
    const start = m.index! + m[1].length;
    if (start > last) parts.push(text.slice(last, start));
    const uname = m[2];
    parts.push(
      <Link key={`m${i++}`} href={`/profile/${encodeURIComponent(uname)}`} style={{ fontWeight: 700, textDecoration: 'underline' }} onClick={(e) => e.stopPropagation()}>
        @{uname}
      </Link>,
    );
    last = start + 1 + uname.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

function timeAgoShort(iso: string): string {
  const s = Math.max(0, (Date.now() - new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z').getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function ChatRoomView({
  roomId,
  symbol = null,
  onPresence,
  onPresenceUsers,
  emptyTitle = 'No messages yet',
  emptySub = 'Say something — the session starts here.',
}: {
  roomId: string | null;
  /** Per-instrument Live trading room (roomId must be null). */
  symbol?: string | null;
  onPresence?: (n: number) => void;
  onPresenceUsers?: (users: PresenceUser[]) => void;
  emptyTitle?: string;
  emptySub?: string;
}) {
  const { user, profile, session } = useAuth();
  const { t } = useLang();
  const router = useRouter();
  const [messages, setMessages] = useState<LiveChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [pendingImage, setPendingImage] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const seenIds = useRef(new Set<string>());
  const blockedIds = useRef(new Set<string>());
  const lastSentAt = useRef(0);
  // Public username only — presence meta is broadcast to everyone, anon included.
  // Kept in a ref (not effect deps): the profile resolves async after mount, and
  // re-running the main effect on it would wipe the chat and churn the channel.
  const presenceMeta = user && profile?.username
    ? { username: profile.username, image: profile.avatarUrl ?? null }
    : undefined;
  const presenceMetaRef = useRef(presenceMeta);
  useEffect(() => {
    presenceMetaRef.current = presenceMeta;
    if (presenceMeta) updateChatPresence(roomId, symbol, presenceMeta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, symbol, user?.id, profile?.username, profile?.avatarUrl]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [onlineUsers, setOnlineUsers] = useState<PresenceUser[]>([]);
  const [showMembers, setShowMembers] = useState(false);
  const [communityMembers, setCommunityMembers] = useState<{ id: string; username: string | null; image: string | null }[] | undefined>(undefined);
  const [reactions, setReactions] = useState<Record<string, ReactionSummary[]>>({});
  const [replyingTo, setReplyingTo] = useState<LiveChatMessage | null>(null);
  const [canPin, setCanPin] = useState(false);
  const [pinned, setPinned] = useState<LiveChatMessage[]>([]);
  const [showPinned, setShowPinned] = useState(true);

  // Zajednica roster for @mention autocomplete — only on the Live room (roomId=null), the one
  // ChatRoomView instance that's actually the members-only community. Elsewhere (DM/group rooms)
  // MentionTextarea falls back to its default sitewide /api/search behaviour.
  useEffect(() => {
    if (roomId !== null || !user) return;
    let alive = true;
    const headers: Record<string, string> = {};
    if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
    fetch('/api/community/members', { headers, credentials: 'include', cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : { members: [] }))
      .then((d) => {
        if (!alive) return;
        const list = (d.members ?? []) as { userId: string; username: string; image: string | null }[];
        setCommunityMembers(list.map((m) => ({ id: m.userId, username: m.username, image: m.image })));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [roomId, user, session]);

  // Member list: everyone currently online + everyone who posted in the messages loaded here
  // (owner request, 2026-09-08 — a "who's in this room" list like other chat apps). Guests
  // (presence keys without a real userId) are excluded; last-seen data wins per user.
  const roomMembers = useMemo(() => {
    const byId = new Map<string, { userId: string; username: string; image: string | null }>();
    for (const u of onlineUsers) {
      if (!u.username || u.key.startsWith('guest-')) continue;
      byId.set(u.key, { userId: u.key, username: u.username, image: u.image ?? null });
    }
    for (const m of messages) {
      if (!m.username || byId.has(m.userId)) continue;
      byId.set(m.userId, { userId: m.userId, username: m.username, image: m.userImage ?? null });
    }
    return [...byId.values()].sort((a, b) => a.username.localeCompare(b.username));
  }, [onlineUsers, messages]);

  // Pin permission + currently pinned messages — Live rooms only (roomId=null). Admin check
  // reuses the forum moderation endpoint (ADMIN_USER_IDS, server-env only); verified-trader
  // check is a DB RPC (can_pin_live_messages).
  const loadPinned = useCallback(() => {
    if (roomId !== null) return;
    fetchPinnedMessages(symbol ?? null).then(setPinned).catch(() => {});
  }, [roomId, symbol]);
  useEffect(() => {
    if (roomId !== null || !user) { setCanPin(false); return; }
    let alive = true;
    const headers: Record<string, string> = {};
    if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
    Promise.all([
      canPinLiveMessages().catch(() => false),
      fetch('/api/forum/moderate', { headers, credentials: 'include', cache: 'no-store' })
        .then((r) => (r.ok ? r.json() : { admin: false }))
        .then((d) => d.admin === true)
        .catch(() => false),
    ]).then(([verified, admin]) => { if (alive) setCanPin(verified || admin); });
    return () => { alive = false; };
  }, [roomId, symbol, user, session]);
  useEffect(() => { loadPinned(); }, [loadPinned]);

  const onTogglePin = useCallback((msg: LiveChatMessage) => {
    const nextPin = !msg.pinnedAt;
    setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, pinnedAt: nextPin ? new Date().toISOString() : null } : m)));
    pinLiveMessage(msg.id, nextPin).then(loadPinned).catch((e: any) => {
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, pinnedAt: msg.pinnedAt } : m)));
      window.alert(e?.message || t('Could not update the pin.'));
    });
  }, [loadPinned, t]);

  // Reactions: fetch summaries for whatever message ids are currently loaded (initial load +
  // every poll tick — reuses ROOM_POLL_MS, no separate realtime channel needed for counts).
  const refreshReactions = useCallback((ids: string[]) => {
    if (!ids.length) return;
    fetchLiveChatReactions(ids).then((map) => setReactions((prev) => ({ ...prev, ...map }))).catch(() => {});
  }, []);

  const onToggleReaction = useCallback((messageId: string, reaction: ReactionSummary['reaction']) => {
    if (!user) { router.push('/sign-up-login-screen'); return; }
    setReactions((prev) => {
      const cur = prev[messageId] ?? [];
      const mutuallyExclusiveWith = reaction === 'like' ? 'dislike' : reaction === 'dislike' ? 'like' : null;
      const existing = cur.find((r) => r.reaction === reaction);
      let next = cur.filter((r) => r.reaction !== reaction && r.reaction !== mutuallyExclusiveWith);
      const opposite = cur.find((r) => r.reaction === mutuallyExclusiveWith);
      if (opposite?.mine) next.push({ ...opposite, count: Math.max(0, opposite.count - 1), mine: false });
      else if (opposite) next.push(opposite);
      if (existing?.mine) {
        next.push({ ...existing, count: Math.max(0, existing.count - 1), mine: false });
      } else {
        next.push({ reaction, count: (existing?.count ?? 0) + 1, mine: true });
      }
      return { ...prev, [messageId]: next.filter((r) => r.count > 0 || r.mine) };
    });
    toggleLiveChatReaction(messageId, reaction)
      .then((summary) => setReactions((prev) => ({ ...prev, [messageId]: summary })))
      .catch(() => refreshReactions([messageId]));
  }, [user, router, refreshReactions]);

  const messagesById = useMemo(() => {
    const map = new Map<string, LiveChatMessage>();
    for (const m of messages) map.set(m.id, m);
    return map;
  }, [messages]);

  const mentionUser = useCallback((username: string) => {
    const tag = `@${username} `;
    setText((prev) => (prev.includes(tag) ? prev : prev + (prev && !prev.endsWith(' ') ? ' ' : '') + tag));
    setTimeout(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }, 0);
  }, []);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  const appendMessage = useCallback(
    (m: LiveChatMessage) => {
      if (seenIds.current.has(m.id)) return;
      seenIds.current.add(m.id);
      if (blockedIds.current.has(m.userId)) return;
      setMessages((prev) => [...prev, m]);
      setTimeout(scrollToBottom, 50);
    },
    [scrollToBottom],
  );

  const mergeRecent = useCallback((rows: LiveChatMessage[]) => {
    const fresh = rows.filter((r) => !seenIds.current.has(r.id));
    if (!fresh.length) return;
    fresh.forEach((r) => seenIds.current.add(r.id));
    const visible = fresh.filter((r) => !blockedIds.current.has(r.userId));
    if (!visible.length) return;
    setMessages((prev) =>
      [...prev, ...visible].sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1)),
    );
  }, []);

  useEffect(() => {
    let alive = true;
    seenIds.current = new Set();
    setMessages([]);
    setLoading(true);

    (user ? fetchBlockedIds() : Promise.resolve(new Set<string>()))
      .then((ids) => {
        if (alive) blockedIds.current = ids;
      })
      .then(() => fetchRecentMessages(roomId, 50, symbol))
      .then((rows) => {
        if (!alive) return;
        mergeRecent(rows.reverse());
        refreshReactions(rows.map((r) => r.id));
        setTimeout(() => bottomRef.current?.scrollIntoView({ block: 'end' }), 80);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));

    const unsubscribe = subscribeToChat({
      roomId,
      symbol,
      presenceKey: user?.id ?? `guest-${Math.random().toString(36).slice(2, 10)}`,
      presenceMeta: presenceMetaRef.current,
      onMessage: appendMessage,
      onPresence,
      onPresenceUsers: (users) => { setOnlineUsers(users); onPresenceUsers?.(users); },
    });

    // Poll fallback: private rooms need it (ES256-token downgrade drops RLS-gated
    // events); for symbol rooms it's cheap insurance for the unfiltered sub.
    const poll = roomId || symbol
      ? setInterval(() => {
          fetchRecentMessages(roomId, 50, symbol).then((rows) => {
            if (!alive) return;
            mergeRecent(rows.reverse());
            refreshReactions(rows.map((r) => r.id));
          }).catch(() => {});
        }, ROOM_POLL_MS)
      : null;

    return () => {
      alive = false;
      if (poll) clearInterval(poll);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, symbol, user?.id]);

  // Thumbnail of the pending image in the composer (object URL, revoked on change).
  useEffect(() => {
    if (!pendingImage) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingImage);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingImage]);

  // One entry point for file picker, Ctrl+V paste and drag-and-drop.
  const attachFile = useCallback(
    (file: File | null | undefined): boolean => {
      if (!file) return false;
      if (!file.type.startsWith('image/')) {
        setError(t('Only images can be pasted here.'));
        return false;
      }
      if (!user) {
        router.push('/sign-up-login-screen');
        return false;
      }
      setError('');
      // Clipboard screenshots arrive as a nameless "image.png" — give them a
      // recognisable name so the composer chip reads well.
      const named = file.name && file.name !== 'image.png'
        ? file
        : new File([file], `chart-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')}.png`, { type: file.type });
      setPendingImage(named);
      setTimeout(() => textareaRef.current?.focus(), 0);
      return true;
    },
    [user, router, t],
  );

  const onPaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const img = items.find((i) => i.kind === 'file' && i.type.startsWith('image/'));
      if (!img) return; // plain text paste — let the textarea handle it
      e.preventDefault();
      attachFile(img.getAsFile());
    },
    [attachFile],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = Array.from(e.dataTransfer?.files ?? []).find((f) => f.type.startsWith('image/'));
      if (file) attachFile(file);
      else if (e.dataTransfer?.files?.length) setError(t('Only images can be pasted here.'));
    },
    [attachFile, t],
  );

  const onReply = useCallback((msg: LiveChatMessage) => {
    setReplyingTo(msg);
    setTimeout(() => textareaRef.current?.focus(), 0);
  }, []);

  const onSend = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed && !pendingImage) return;
    if (!user) {
      router.push('/sign-up-login-screen');
      return;
    }
    if (Date.now() - lastSentAt.current < SEND_COOLDOWN_MS) return;

    setSending(true);
    setError('');
    try {
      let imageUrl: string | undefined;
      if (pendingImage) {
        imageUrl = (await uploadChatImage(pendingImage, roomId ? 'room' : 'live')).url;
      }
      const row = await sendMessage({ text: trimmed || undefined, imageUrl, roomId, symbol, replyToId: replyingTo?.id ?? null });
      lastSentAt.current = Date.now();
      appendMessage(row);
      setText('');
      setPendingImage(null);
      setReplyingTo(null);
    } catch (e: any) {
      setError(e?.message || t('Could not send the message.'));
    } finally {
      setSending(false);
    }
  }, [text, pendingImage, user, roomId, symbol, replyingTo, appendMessage, router, t]);

  useEffect(() => {
    if (!viewerUrl) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setViewerUrl(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewerUrl]);

  const onReport = useCallback((msg: LiveChatMessage) => {
    const reason = window.prompt(`${t('Report message by')} @${msg.username} — ${t('why? (Spam / Abusive / Other)')}`, 'Spam');
    if (!reason) return;
    reportMessage(msg.id, reason)
      .then(() => window.alert(t('Reported. Our team will review this message.')))
      .catch((e: any) => window.alert(e?.message || t('Could not send the report.')));
  }, [t]);

  const onBlock = useCallback((msg: LiveChatMessage) => {
    if (!window.confirm(`${t('Block')} @${msg.username}? ${t('You will no longer see their content, and they cannot DM you.')}`)) return;
    blockUser(msg.userId)
      .then(() => {
        blockedIds.current.add(msg.userId);
        setMessages((prev) => prev.filter((m) => m.userId !== msg.userId));
      })
      .catch((e: any) => window.alert(e?.message || t('Could not block this user.')));
  }, [t]);

  return (
    <div
      className="relative flex flex-col h-full min-h-0"
      onDragOver={(e) => {
        if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
        e.preventDefault();
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setDragOver(false);
      }}
      onDrop={onDrop}
    >
      {dragOver ? (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none"
          style={{ background: 'color-mix(in srgb, var(--accent) 12%, transparent)', border: '2px dashed var(--accent)', borderRadius: 16 }}
        >
          <div className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold" style={{ background: 'var(--bg-card)', color: 'var(--accent)' }}>
            <ImagePlus size={16} /> {t('Drop the image to attach it')}
          </div>
        </div>
      ) : null}

      {/* Pinned — verified traders/admins can pin a message (e.g. a signal) to the top of a
          Live room. Collapsible like Members below. */}
      {roomId === null && pinned.length > 0 && (
        <div className="border-b" style={{ borderColor: 'var(--border)' }}>
          <button
            onClick={() => setShowPinned((s) => !s)}
            className="w-full flex items-center gap-2 px-4 py-2 text-xs font-bold"
            style={{ color: 'var(--accent)' }}
          >
            <Pin size={13} /> {t('Pinned')} ({pinned.length})
            {showPinned ? <ChevronUp size={13} className="ml-auto" /> : <ChevronDown size={13} className="ml-auto" />}
          </button>
          {showPinned && (
            <div className="px-2 pb-2 max-h-40 overflow-y-auto space-y-1">
              {pinned.map((p) => (
                <div key={p.id} className="flex items-start gap-2 px-2 py-1.5 rounded-lg" style={{ background: 'var(--bg-sunken)' }}>
                  <div className="min-w-0 flex-1">
                    <span className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>@{p.username}</span>
                    <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{p.text || (p.imageUrl ? `📈 ${t('chart')}` : '')}</p>
                  </div>
                  {canPin && (
                    <button onClick={() => onTogglePin(p)} title={t('Unpin')} className="p-1 rounded shrink-0" style={{ color: 'var(--text-secondary)' }}>
                      <PinOff size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Members — everyone online + everyone who's posted here. Click a name for their
          profile; the @ button drops a mention into the composer to pull them into the chat. */}
      {roomMembers.length > 0 && (
        <div className="border-b" style={{ borderColor: 'var(--border)' }}>
          <button
            onClick={() => setShowMembers((s) => !s)}
            className="w-full flex items-center gap-2 px-4 py-2 text-xs font-bold"
            style={{ color: 'var(--text-secondary)' }}
          >
            <Users size={13} /> {t('Members')} ({roomMembers.length})
            {showMembers ? <ChevronUp size={13} className="ml-auto" /> : <ChevronDown size={13} className="ml-auto" />}
          </button>
          {showMembers && (
            <div className="px-2 pb-2 max-h-40 overflow-y-auto">
              {roomMembers.map((m) => (
                <div key={m.userId} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-black/5">
                  <Link href={`/profile/${encodeURIComponent(m.username)}`} className="flex items-center gap-2 flex-1 min-w-0">
                    {m.image ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={m.image} alt={m.username} className="w-6 h-6 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0" style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>
                        {m.username[0].toUpperCase()}
                      </div>
                    )}
                    <span className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>@{m.username}</span>
                  </Link>
                  {user && m.userId !== user.id && (
                    <button onClick={() => mentionUser(m.username)} title={t('Mention in chat')} className="p-1.5 rounded-lg shrink-0" style={{ color: 'var(--text-secondary)' }}>
                      <AtSign size={13} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-16">
            <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{t(emptyTitle)}</p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{t(emptySub)}</p>
          </div>
        ) : (
          messages.map((m) => {
            const isOwn = m.userId === user?.id;
            const quoted = m.replyToId ? messagesById.get(m.replyToId) : null;
            const myReactions = reactions[m.id] ?? [];
            return (
            <div key={m.id} className="group flex gap-3">
              <Link href={`/profile/${encodeURIComponent(m.username)}`} className="shrink-0">
                {m.userImage ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={m.userImage} alt={m.username} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>
                    {(m.username || '?')[0].toUpperCase()}
                  </div>
                )}
              </Link>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  {m.pinnedAt ? <Pin size={11} style={{ color: 'var(--accent)' }} /> : null}
                  <span className="text-sm font-bold truncate" style={{ color: isOwn ? 'var(--accent)' : 'var(--text-primary)' }}>
                    {m.username || t('trader')}
                  </span>
                  <span className="text-[11px] font-mono-nums" style={{ color: 'var(--text-muted)' }}>{timeAgoShort(m.createdAt)}</span>
                  {user ? (
                    <span className="hidden group-hover:flex items-center gap-2 ml-1">
                      <button onClick={() => onReply(m)} title={t('Reply')} className="opacity-70 hover:opacity-100">
                        <CornerUpLeft size={12} style={{ color: 'var(--text-secondary)' }} />
                      </button>
                      {!isOwn ? (
                        <>
                          <button onClick={() => onReport(m)} title={t('Report')} className="opacity-70 hover:opacity-100">
                            <Flag size={12} style={{ color: 'var(--text-secondary)' }} />
                          </button>
                          <button onClick={() => onBlock(m)} title={t('Block')} className="opacity-70 hover:opacity-100">
                            <Ban size={12} style={{ color: 'var(--text-secondary)' }} />
                          </button>
                        </>
                      ) : null}
                      {canPin ? (
                        <button onClick={() => onTogglePin(m)} title={m.pinnedAt ? t('Unpin') : t('Pin')} className="opacity-70 hover:opacity-100">
                          {m.pinnedAt ? <PinOff size={12} style={{ color: 'var(--accent)' }} /> : <Pin size={12} style={{ color: 'var(--text-secondary)' }} />}
                        </button>
                      ) : null}
                    </span>
                  ) : null}
                </div>
                {m.replyToId ? (
                  <div className="mt-1 pl-2 border-l-2 max-w-full" style={{ borderColor: 'var(--border)' }}>
                    <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                      {quoted ? `↪ @${quoted.username}: ${quoted.text || (quoted.imageUrl ? `📈 ${t('chart')}` : '')}` : `↪ ${t('Replying to a message')}`}
                    </p>
                  </div>
                ) : null}
                {m.text ? (
                  <div
                    className="inline-block mt-1 rounded-2xl px-3 py-2 max-w-full"
                    style={{ background: isOwn ? 'var(--accent)' : 'var(--bg-sunken)', color: isOwn ? '#ffffff' : 'var(--text-primary)' }}
                  >
                    <p className="text-sm break-words whitespace-pre-wrap"><Linkified text={m.text} /></p>
                  </div>
                ) : null}
                {m.imageUrl ? (
                  <button onClick={() => setViewerUrl(m.imageUrl)} className="block" title={t('View full size')}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={m.imageUrl} alt="chart" className="mt-2 rounded-lg max-h-64 border" style={{ borderColor: 'var(--border)' }} />
                  </button>
                ) : null}
                <div className="flex items-center gap-1 mt-1">
                  {REACTIONS.map((r) => {
                    const found = myReactions.find((x) => x.reaction === r.key);
                    if (!found?.count && !user) return null;
                    return (
                      <button
                        key={r.key}
                        onClick={() => onToggleReaction(m.id, r.key)}
                        disabled={!user}
                        title={t(r.label)}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-mono-nums transition-colors duration-100 disabled:cursor-default"
                        style={{
                          background: found?.mine ? 'var(--accent-muted)' : 'transparent',
                          color: found?.mine ? 'var(--accent)' : 'var(--text-muted)',
                          border: `1px solid ${found?.mine ? 'var(--accent)' : 'var(--border)'}`,
                        }}
                      >
                        <span>{r.emoji}</span>
                        {found?.count ? <span>{found.count}</span> : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="border-t px-3 py-3" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)' }}>
        {error ? <p className="text-xs mb-2" style={{ color: 'var(--bearish)' }}>{error}</p> : null}
        {replyingTo ? (
          <div className="flex items-center gap-2 mb-2 p-1.5 pl-2.5 rounded-xl border-l-2" style={{ background: 'var(--bg-sunken)', borderColor: 'var(--accent)' }}>
            <CornerUpLeft size={13} style={{ color: 'var(--accent)' }} className="shrink-0" />
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold" style={{ color: 'var(--accent)' }}>{t('Replying to')} @{replyingTo.username}</span>
              <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{replyingTo.text || (replyingTo.imageUrl ? `📈 ${t('chart')}` : '')}</p>
            </div>
            <button onClick={() => setReplyingTo(null)} title={t('Cancel reply')} className="p-1.5 rounded-lg shrink-0" style={{ color: 'var(--text-secondary)' }}>
              <X size={14} />
            </button>
          </div>
        ) : null}
        {pendingImage ? (
          <div className="flex items-center gap-3 mb-2 p-1.5 rounded-xl" style={{ background: 'var(--bg-sunken)' }}>
            {previewUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={previewUrl} alt="" className="h-14 w-20 rounded-lg object-cover border" style={{ borderColor: 'var(--border)' }} />
            ) : null}
            <span className="truncate text-xs flex-1" style={{ color: 'var(--text-secondary)' }}>📈 {pendingImage.name}</span>
            <button onClick={() => setPendingImage(null)} title={t('Remove image')} className="p-1.5 rounded-lg" style={{ color: 'var(--text-secondary)' }}>
              <X size={14} />
            </button>
          </div>
        ) : null}
        <div className="flex items-end gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              attachFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <button
            onClick={() => (user ? fileRef.current?.click() : router.push('/sign-up-login-screen'))}
            className="p-2.5 rounded-lg transition-colors duration-150"
            style={{ color: 'var(--text-secondary)' }}
            title={t('Attach chart image')}
          >
            <ImagePlus size={19} />
          </button>
          <MentionTextarea
            ref={textareaRef}
            value={text}
            onValueChange={setText}
            users={communityMembers}
            onPaste={onPaste}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder={user ? t('Message…') : t('Sign in to chat')}
            maxLength={MAX_MESSAGE_LEN}
            rows={1}
            className="input-base flex-1 resize-none px-4 py-2.5 text-sm"
            style={{ maxHeight: 120 }}
          />
          <button
            onClick={onSend}
            disabled={sending || (!text.trim() && !pendingImage)}
            className="btn-primary p-2.5 disabled:opacity-40"
            title={t('Send')}
          >
            <Send size={18} />
          </button>
        </div>
        {user && !pendingImage ? (
          <p className="hidden sm:block text-[11px] mt-1.5 px-1" style={{ color: 'var(--text-muted)' }}>
            {t('Paste a screenshot (Ctrl+V) or drop an image to comment on a chart.')}
          </p>
        ) : null}
      </div>

      {/* Image lightbox */}
      {viewerUrl ? (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.95)' }}
          onClick={() => setViewerUrl(null)}
        >
          <button
            onClick={() => setViewerUrl(null)}
            className="absolute top-4 right-4 p-2 rounded-full"
            style={{ background: 'rgba(255,255,255,0.1)', color: '#ffffff' }}
            title={t('Close')}
          >
            <X size={20} />
          </button>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={viewerUrl}
            alt="chart"
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : null}
    </div>
  );
}
